// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getDefaultDateRange } from '../../services/sankeyService';

// 🎯 Unidades de consumo permitidas para Sankey
const UNIDADES_CONSUMO = ['KWh', 'kwh', 'M3', 'm3', 'L', 'l', 'Lt', 'lt', 'Litros', 'litros'];

const SankeyControls = ({ 
  onParamsChange, 
  initialParams = {}, 
  showUnidadesFilter = true,
  showDateControl = true 
}) => {
  const defaultRange = getDefaultDateRange();
  
  // ✅ Filtrar unidades iniciales para incluir solo las de consumo
  const initialUnidades = initialParams.unidades 
    ? initialParams.unidades.filter(u => 
        UNIDADES_CONSUMO.some(allowed => 
          allowed.toLowerCase() === u.toLowerCase()
        )
      )
    : ['KWh', 'M3', 'L']; // Valores por defecto para consumo
  
  const [params, setParams] = useState({
    fechaInicio: initialParams.fechaInicio || defaultRange.fechaInicio,
    fechaFin: initialParams.fechaFin || defaultRange.fechaFin,
    unidades: initialUnidades
  });

  const [unidadesInput, setUnidadesInput] = useState(
    initialUnidades.length > 0 ? initialUnidades.join(',') : 'KWh,M3,L'
  );

  // Estado para rastrear qué preset está activo
  const [activePreset, setActivePreset] = useState(null);

  const handleFechaInicioChange = (e) => {
    const newFechaInicio = e.target.value;
    const newParams = { ...params, fechaInicio: newFechaInicio };
    setParams(newParams);
    setActivePreset(null); // Limpiar preset activo cuando se cambia manualmente
    onParamsChange(newParams);
  };

  const handleFechaFinChange = (e) => {
    const newFechaFin = e.target.value;
    const newParams = { ...params, fechaFin: newFechaFin };
    setParams(newParams);
    setActivePreset(null); // Limpiar preset activo cuando se cambia manualmente
    onParamsChange(newParams);
  };

  const handleUnidadesChange = (e) => {
    const value = e.target.value;
    setUnidadesInput(value);
    
    // ✅ Procesar y filtrar solo unidades de consumo permitidas
    const unidadesArray = value
      .split(',')
      .map(u => u.trim())
      .filter(u => u.length > 0)
      .filter(u => 
        // Solo permitir unidades de consumo
        UNIDADES_CONSUMO.some(allowed => 
          allowed.toLowerCase() === u.toLowerCase()
        )
      );
    
    const newParams = { ...params, unidades: unidadesArray };
    setParams(newParams);
    onParamsChange(newParams);
  };

  const setPresetRange = (days) => {
    const fechaFin = new Date();
    const fechaInicio = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    
    const newParams = {
      ...params,
      fechaInicio: fechaInicio.toISOString().split('T')[0],
      fechaFin: fechaFin.toISOString().split('T')[0]
    };
    
    setParams(newParams);
    setActivePreset(days); // Marcar este preset como activo
    onParamsChange(newParams);
  };

  const resetToDefaults = () => {
    const defaultParams = {
      fechaInicio: defaultRange.fechaInicio,
      fechaFin: defaultRange.fechaFin,
      unidades: []
    };
    setParams(defaultParams);
    setUnidadesInput('');
    setActivePreset(null); // Limpiar preset activo
    onParamsChange(defaultParams);
  };

  // Función para obtener estilos del botón de preset
  const getPresetButtonStyle = (days) => {
    const isActive = activePreset === days;
    return {
      padding: '0.25rem 0.75rem',
      backgroundColor: isActive ? '#007bff' : '#e9ecef',
      color: isActive ? 'white' : '#495057',
      border: '1px solid #ced4da',
      borderRadius: '20px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    };
  };

  const handlePresetButtonInteraction = (e, days, isHover) => {
    const isActive = activePreset === days;
    if (isHover && !isActive) {
      e.target.style.backgroundColor = '#007bff';
      e.target.style.color = 'white';
    } else if (!isHover && !isActive) {
      e.target.style.backgroundColor = '#e9ecef';
      e.target.style.color = '#495057';
    }
  };

  return (
    <div className="sankey-controls" style={{ 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
      borderRadius: '12px', 
      padding: '1.5rem',
      marginBottom: '1.5rem',
      border: '1px solid #dee2e6',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h4 style={{ 
        marginBottom: '1.5rem', 
        color: '#495057', 
        fontSize: '1.1rem',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span style={{ fontSize: '1.2rem' }}>⚙️</span>
        Configuración de Gráfica Sankey
      </h4>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '1.5rem',
        alignItems: 'start'
      }}>
        {showDateControl && (
          <div style={{ 
            backgroundColor: 'white',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h5 style={{ 
              margin: '0 0 1rem 0', 
              color: '#495057', 
              fontSize: '0.95rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Rango de Fechas
            </h5>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="fechaInicio" style={{ 
                    fontSize: '0.85rem', 
                    marginBottom: '0.25rem', 
                    color: '#6c757d',
                    display: 'block',
                    fontWeight: '500'
                  }}>
                    Desde:
                  </label>
                  <input
                    id="fechaInicio"
                    type="date"
                    value={params.fechaInicio}
                    onChange={handleFechaInicioChange}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#80bdff'}
                    onBlur={(e) => e.target.style.borderColor = '#ced4da'}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <label htmlFor="fechaFin" style={{ 
                    fontSize: '0.85rem', 
                    marginBottom: '0.25rem', 
                    color: '#6c757d',
                    display: 'block',
                    fontWeight: '500'
                  }}>
                    Hasta:
                  </label>
                  <input
                    id="fechaFin"
                    type="date"
                    value={params.fechaFin}
                    onChange={handleFechaFinChange}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#80bdff'}
                    onBlur={(e) => e.target.style.borderColor = '#ced4da'}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                <button
                  onClick={() => setPresetRange(1)}
                  style={getPresetButtonStyle(1)}
                  onMouseOver={(e) => handlePresetButtonInteraction(e, 1, true)}
                  onMouseOut={(e) => handlePresetButtonInteraction(e, 1, false)}
                  onFocus={(e) => handlePresetButtonInteraction(e, 1, true)}
                  onBlur={(e) => handlePresetButtonInteraction(e, 1, false)}
                >
                  Último día
                </button>
                <button
                  onClick={() => setPresetRange(7)}
                  style={getPresetButtonStyle(7)}
                  onMouseOver={(e) => handlePresetButtonInteraction(e, 7, true)}
                  onMouseOut={(e) => handlePresetButtonInteraction(e, 7, false)}
                  onFocus={(e) => handlePresetButtonInteraction(e, 7, true)}
                  onBlur={(e) => handlePresetButtonInteraction(e, 7, false)}
                >
                  Última semana
                </button>
                <button
                  onClick={() => setPresetRange(30)}
                  style={getPresetButtonStyle(30)}
                  onMouseOver={(e) => handlePresetButtonInteraction(e, 30, true)}
                  onMouseOut={(e) => handlePresetButtonInteraction(e, 30, false)}
                  onFocus={(e) => handlePresetButtonInteraction(e, 30, true)}
                  onBlur={(e) => handlePresetButtonInteraction(e, 30, false)}
                >
                  Último mes
                </button>
              </div>
            </div>
          </div>
        )}

        {showUnidadesFilter && (
          <div style={{ 
            backgroundColor: 'white',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h5 style={{ 
              margin: '0 0 1rem 0', 
              color: '#495057', 
              fontSize: '0.95rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Filtro de Unidades
            </h5>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label htmlFor="unidades" style={{ 
                fontSize: '0.85rem', 
                marginBottom: '0.25rem', 
                color: '#6c757d',
                fontWeight: '500'
              }}>
                Tipos de sensores (separados por coma):
              </label>
              <input
                id="unidades"
                type="text"
                placeholder="Ej: KWh,M3,L (Solo unidades de consumo)"
                value={unidadesInput}
                onChange={handleUnidadesChange}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={(e) => e.target.style.borderColor = '#80bdff'}
                onBlur={(e) => e.target.style.borderColor = '#ced4da'}
              />
              <div style={{ fontSize: '0.8rem', color: '#6c757d', lineHeight: '1.3' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>Búsqueda flexible:</strong> ignora mayúsculas/minúsculas
                </div>
                <div>
                  <strong>Ejemplos:</strong> &quot;kwh&quot; encuentra &quot;Kwh&quot;, &quot;KWH&quot;, &quot;kWh&quot; | &quot;temp&quot; encuentra &quot;temperatura&quot;, &quot;Temp&quot;
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginTop: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e9ecef'
      }}>
        <button
          onClick={resetToDefaults}
          style={{
            padding: '0.6rem 1.5rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
          onFocus={(e) => e.target.style.backgroundColor = '#5a6268'}
          onBlur={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          Limpiar filtros
        </button>
      </div>
    </div>
  );
};

SankeyControls.propTypes = {
  onParamsChange: PropTypes.func.isRequired,
  initialParams: PropTypes.shape({
    fechaInicio: PropTypes.string,
    fechaFin: PropTypes.string,
    unidades: PropTypes.arrayOf(PropTypes.string)
  }),
  showUnidadesFilter: PropTypes.bool,
  showDateControl: PropTypes.bool
};

SankeyControls.defaultProps = {
  initialParams: {},
  showUnidadesFilter: true,
  showDateControl: true
};

export default SankeyControls;